import { useState } from 'react'
import { formatCount, formatLatency } from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import type { AgentStep } from '../lib'
import { useDocumentPane } from '../model'
import { ContentBlock } from './content-block'
import { Disclosure } from './disclosure'
import { JsonBlock } from './json-block'
import { LongText } from './long-text'
import { MaterialIcon, Muted, PageLink } from './primitives'

function formatElapsed(ms: number | null): string {
  if (ms === null) return ''
  const seconds = Math.round(ms / 1000)
  return `+${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

function StepDetail({ step, toolNames }: { step: AgentStep; toolNames: Record<string, string> }) {
  const { showQuote } = useDocumentPane()
  const call = step.call

  return (
    <div className="flex flex-col gap-3 pt-1 pb-3">
      {step.thinking && (
        <Disclosure label="The agent's thinking">
          <div className="border-divider border-l-2 pl-3">
            <LongText text={step.thinking} />
          </div>
        </Disclosure>
      )}
      {step.text && (
        <div className="flex flex-col gap-1">
          <Muted>The agent wrote</Muted>
          <LongText text={step.text} />
        </div>
      )}
      {(step.pages?.length || step.searchQuery) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {step.searchQuery && (
            <button
              type="button"
              onClick={() => showQuote(step.searchQuery!, { isSearch: true })}
              className="text-text-link flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[12px] leading-4 hover:underline"
            >
              <MaterialIcon
                name="manage_search"
                className="text-[16px]"
              />
              Find in the document
            </button>
          )}
          {step.pages && step.pages.length > 0 && (
            <>
              <Muted>Open</Muted>
              {step.pages.slice(0, 12).map((page) => (
                <PageLink
                  key={page}
                  page={page}
                />
              ))}
            </>
          )}
        </div>
      )}
      {step.kind === 'tool' && (
        <JsonBlock
          value={step.input}
          label="Request"
        />
      )}
      {step.result && (
        <div className="flex flex-col gap-1">
          <Muted>Response</Muted>
          <ContentBlock
            block={step.result}
            toolNames={toolNames}
            hideHeader
          />
        </div>
      )}
      {step.kind === 'tool' && !step.result && <Muted>No response was stored.</Muted>}
      {(step.kind === 'document' || step.kind === 'nudge') &&
        step.blocks?.map((block, index) => (
          <ContentBlock
            key={index}
            block={block}
            toolNames={toolNames}
          />
        ))}
      {call && (
        <Muted>
          {[
            `Model call: ${formatLatency(call.latency_ms)}`,
            `${formatCount(call.input_tokens)} tokens in`,
            `${formatCount(call.output_tokens)} out`,
            `${formatCount(call.cache_read_tokens)} from cache`,
            call.stop_reason ? `stopped on ${call.stop_reason}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Muted>
      )}
    </div>
  )
}

export function AgentStepRow({
  step,
  toolNames,
}: {
  step: AgentStep
  toolNames: Record<string, string>
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <li className="border-divider border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="hover:bg-surface-low grid w-full cursor-pointer grid-cols-[44px_22px_minmax(0,1fr)_20px] items-start gap-x-2 rounded-md border-none bg-transparent px-2 py-2 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-text-tertiary pt-0.5 font-mono text-[11px] leading-4 tabular-nums">
          {formatElapsed(step.elapsedMs)}
        </span>
        <MaterialIcon
          name={step.icon}
          className={cn('mt-px text-[18px]', {
            'text-alert-error-content': step.tone === 'error',
            'text-success-content': step.tone === 'success',
            'text-text-secondary': !step.tone,
          })}
        />
        <span className="flex min-w-0 flex-col">
          <span className="text-text-primary text-[13px] leading-5 wrap-anywhere">
            {step.request}
          </span>
          {step.response && (
            <span
              className={cn('flex items-start gap-1 text-[12px] leading-4 wrap-anywhere', {
                'text-alert-error-content': step.tone === 'error',
                'text-success-content': step.tone === 'success',
                'text-text-secondary': !step.tone,
              })}
            >
              <MaterialIcon
                name="subdirectory_arrow_right"
                className="text-[14px] opacity-60"
              />
              {step.response}
            </span>
          )}
        </span>
        <MaterialIcon
          name={isOpen ? 'expand_less' : 'expand_more'}
          className="text-text-tertiary text-[18px]"
        />
      </button>
      {isOpen && (
        <div className="pr-2 pl-[84px]">
          <StepDetail
            step={step}
            toolNames={toolNames}
          />
        </div>
      )}
    </li>
  )
}
